import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseControl } from './base-control';

/**
 * Checkbox with label (design system CheckboxWLabel, figma 342:3278).
 * ControlValueAccessor over a boolean; native <input type="checkbox"> under
 * the hood. Supports the indeterminate ("some selected") visual state via the
 * `indeterminate` input. Label via the `label` input or projected content;
 * selected label is semibold per spec.
 *
 *   <app-checkbox [(ngModel)]="acceptTerms">{{ t('key') }}</app-checkbox>
 */
@Component({
  selector: 'app-checkbox',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Checkbox), multi: true },
  ],
  template: `
    <label class="row" [class.disabled]="disabled()">
      <input
        type="checkbox"
        class="native"
        [checked]="!!value()"
        [indeterminate]="indeterminate()"
        [disabled]="disabled()"
        (change)="onToggle($event)"
        (blur)="onTouched()"
      />
      <span class="box" [class.filled]="filled()" aria-hidden="true">
        @if (indeterminate()) {
          <svg width="8" height="2" viewBox="0 0 8 2">
            <rect width="8" height="2" fill="currentColor" />
          </svg>
        } @else if (value()) {
          <svg width="12" height="10" viewBox="0 0 11.44 9.13">
            <path
              d="M 11.439 1.361 L 4.939 8.361 L 4.233 9.121 L 0 4.888 L 1.414 3.474 L 4.18 6.239 L 9.975 0 L 11.439 1.361 Z"
              fill="currentColor"
            />
          </svg>
        }
      </span>
      <span class="label" [class.selected]="filled()">{{ label() }}<ng-content /></span>
    </label>
  `,
  styles: `
    .row {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      height: 32px;
      cursor: pointer;
    }
    .row.disabled {
      cursor: default;
      opacity: 0.5;
    }

    /* Visually hidden but focusable; the focus ring is drawn on the box. */
    .native {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: 0;
      opacity: 0;
      pointer-events: none;
    }
    .native:focus-visible + .box {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
    }

    .box {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 18px;
      height: 18px;
      border-radius: 4px;
      background: var(--surface-3);
      box-shadow: var(--shadow-hairline);
      color: var(--action-text);
      transition: background-color 0.15s ease;
    }
    .box.filled {
      background: var(--action-fill);
    }
    .row:not(.disabled):hover .box.filled {
      background: var(--papercast-blue);
    }
    .row:not(.disabled):hover .box:not(.filled) {
      background:
        linear-gradient(var(--overlay-down-1), var(--overlay-down-1)),
        linear-gradient(var(--surface-3), var(--surface-3));
      box-shadow: inset 0 0 0 1px var(--core-overlay-200);
    }

    .label {
      font:
        var(--font-weight-regular) var(--text-body-1) / var(--text-body-1--line-height)
        var(--font-body);
      color: var(--ink-1);
    }
    .label.selected {
      font-weight: var(--font-weight-semibold);
      line-height: 20px;
    }
    .label:empty {
      display: none;
    }
  `,
})
export class Checkbox extends BaseControl<boolean> {
  /** "Some selected" visual state — controlled by the parent, not the value. */
  readonly indeterminate = input(false, { transform: booleanAttribute });
  /** Label text; alternatively project content into the element. */
  readonly label = input<string>('');

  protected readonly filled = computed(() => !!this.value() || this.indeterminate());

  protected onToggle(event: Event): void {
    this.setValue((event.target as HTMLInputElement).checked);
  }
}
